import { GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import {
    A4_CONTENT_END_ROW,
    A4_CONTENT_START_ROW,
    A4_ROWS_PER_PAGE,
    CELL_MARGIN,
    COLUMNS,
} from 'adapter/in/dashboards/components/Grid/constants';
import { LayoutElementType, LayoutType } from 'domain/entity/layout';

export const NEW_WIDGET_MIN_WIDTH = 2;
export const NEW_WIDGET_MIN_HEIGHT = 2;
export const DEFAULT_TEXT_BLOCK_MIN_WIDTH = 6;
export const DEFAULT_TEXT_BLOCK_MIN_HEIGHT = 4;
export const DEFAULT_ANALYSIS_MIN_WIDTH = 10;
export const DEFAULT_ANALYSIS_MIN_HEIGHT = 10;

export enum NewWidgetType {
    ANALYSIS = 'ANALYSIS',
    RICH_TEXT = 'RICH_TEXT',
    BUSINESS_INSIGHT = 'BUSINESS_INSIGHT',
}

export type GridCell = {
    x: number;
    y: number;
};

export type NewWidgetSizeConstraints = {
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
};

export type WidgetCoordinates = GridCell & {
    w: number;
    h: number;
};

type CoordinatesElement = WidgetCoordinates;

export type AvailableWidgetCoordinates = {
    type: NewWidgetType;
    coordinates: WidgetCoordinates;
};

type CoordinatesConstraintsByType = Partial<Record<NewWidgetType, NewWidgetSizeConstraints>>;

export const DEFAULT_COORDINATES_CONSTRAINTS: NewWidgetSizeConstraints = {
    minWidth: NEW_WIDGET_MIN_WIDTH,
    minHeight: NEW_WIDGET_MIN_HEIGHT,
    maxWidth: COLUMNS[LayoutType.A4],
    maxHeight: COLUMNS[LayoutType.A4],
};

export const getWidgetCoordinatesConstraintsFromLayout = (layout: GridLayoutElement[]): CoordinatesConstraintsByType => {
    const analysisElement = layout.find((element) => element.type === LayoutElementType.ANALYSIS);
    const richTextElement = layout.find((element) => element.type === LayoutElementType.RICH_TEXT);
    const businessInsightElement = layout.find((element) => element.type === LayoutElementType.BUSINESS_INSIGHT);

    return {
        [NewWidgetType.ANALYSIS]: analysisElement ? getCoordinatesConstraints(analysisElement) : DEFAULT_COORDINATES_CONSTRAINTS,
        [NewWidgetType.RICH_TEXT]: richTextElement ? getCoordinatesConstraints(richTextElement) : DEFAULT_COORDINATES_CONSTRAINTS,
        [NewWidgetType.BUSINESS_INSIGHT]: businessInsightElement
            ? getCoordinatesConstraints(businessInsightElement)
            : DEFAULT_COORDINATES_CONSTRAINTS,
    };
};

export const getGridCellFromPointerPosition = (
    pointerX: number,
    pointerY: number,
    cellSize: { width: number; height: number }
): GridCell | null => {
    if (cellSize.width <= 0 || cellSize.height <= 0 || pointerX < 0 || pointerY < 0) return null;

    const columnWidth = cellSize.width + CELL_MARGIN;
    const rowHeight = cellSize.height + CELL_MARGIN;
    const x = Math.floor(pointerX / columnWidth);
    const y = Math.floor(pointerY / rowHeight);
    const xInsideCell = pointerX - x * columnWidth;
    const yInsideCell = pointerY - y * rowHeight;

    if (x < 0 || x >= COLUMNS[LayoutType.A4] || xInsideCell > cellSize.width || yInsideCell > cellSize.height) {
        return null;
    }

    return { x, y };
};

export const getAvailableWidgetCoordinates = (layout: GridLayoutElement[], cell: GridCell): AvailableWidgetCoordinates[] => {
    const constraintsByType = getWidgetCoordinatesConstraintsFromLayout(layout);

    return ([NewWidgetType.ANALYSIS, NewWidgetType.RICH_TEXT, NewWidgetType.BUSINESS_INSIGHT] as NewWidgetType[])
        .map((type) => {
            const constraints = constraintsByType[type];
            if (!constraints) return null;
            const coordinates = findAvailableCoordinatesContainingCell(layout, cell, constraints);
            if (!coordinates) return null;

            return {
                type,
                coordinates,
            };
        })
        .filter((coordinates): coordinates is AvailableWidgetCoordinates => coordinates !== null);
};

export const getCoordinatesPreview = (availableCoordinates: AvailableWidgetCoordinates[]): WidgetCoordinates | null => {
    if (availableCoordinates.length === 0) return null;

    const minX = Math.min(...availableCoordinates.map(({ coordinates }) => coordinates.x));
    const minY = Math.min(...availableCoordinates.map(({ coordinates }) => coordinates.y));
    const maxX = Math.max(...availableCoordinates.map(({ coordinates }) => coordinates.x + coordinates.w));
    const maxY = Math.max(...availableCoordinates.map(({ coordinates }) => coordinates.y + coordinates.h));

    return {
        x: minX,
        y: minY,
        w: maxX - minX,
        h: maxY - minY,
    };
};

export const getWidgetCreationCoordinates = (
    layout: CoordinatesElement[],
    type: NewWidgetType,
    minimumCoordinates: WidgetCoordinates
): WidgetCoordinates => {
    const defaultCoordinates = {
        ...minimumCoordinates,
        ...getDefaultWidgetSize(type),
    };

    return canUseCoordinates(layout, defaultCoordinates) ? defaultCoordinates : minimumCoordinates;
};

export const canUseCoordinates = (layout: CoordinatesElement[], coordinates: WidgetCoordinates): boolean =>
    coordinates.x >= 0 &&
    coordinates.y >= 0 &&
    coordinates.x + coordinates.w <= COLUMNS[LayoutType.A4] &&
    isInsideA4ContentRows(coordinates) &&
    !layout.some((element) => coordinatesOverlap(coordinates, element));

const findAvailableCoordinatesContainingCell = (
    layout: GridLayoutElement[],
    cell: GridCell,
    constraints: NewWidgetSizeConstraints
): WidgetCoordinates | null => {
    const minX = cell.x - constraints.minWidth + 1;
    const minY = cell.y - constraints.minHeight + 1;

    for (let y = cell.y; y >= minY; y -= 1) {
        for (let x = cell.x; x >= minX; x -= 1) {
            const coordinates = {
                x,
                y,
                w: constraints.minWidth,
                h: constraints.minHeight,
            };

            if (canUseCoordinates(layout, coordinates)) {
                return coordinates;
            }
        }
    }

    return null;
};

const getCoordinatesConstraints = (element: GridLayoutElement): NewWidgetSizeConstraints => ({
    minWidth: NEW_WIDGET_MIN_WIDTH,
    minHeight: NEW_WIDGET_MIN_HEIGHT,
    maxWidth: element.maxWidth,
    maxHeight: element.maxHeight,
});

const getDefaultWidgetSize = (type: NewWidgetType): Pick<WidgetCoordinates, 'w' | 'h'> => {
    switch (type) {
        case NewWidgetType.ANALYSIS:
            return { w: DEFAULT_ANALYSIS_MIN_WIDTH, h: DEFAULT_ANALYSIS_MIN_HEIGHT };
        case NewWidgetType.RICH_TEXT:
        case NewWidgetType.BUSINESS_INSIGHT:
            return { w: DEFAULT_TEXT_BLOCK_MIN_WIDTH, h: DEFAULT_TEXT_BLOCK_MIN_HEIGHT };
        default:
            return { w: NEW_WIDGET_MIN_WIDTH, h: NEW_WIDGET_MIN_HEIGHT };
    }
};

const isInsideA4ContentRows = (coordinates: WidgetCoordinates): boolean => {
    const startRow = coordinates.y % A4_ROWS_PER_PAGE;
    const endRow = startRow + coordinates.h - 1;

    return startRow >= A4_CONTENT_START_ROW && endRow <= A4_CONTENT_END_ROW;
};

const coordinatesOverlap = (coordinates: WidgetCoordinates, element: WidgetCoordinates): boolean =>
    coordinates.x < element.x + element.w &&
    coordinates.x + coordinates.w > element.x &&
    coordinates.y < element.y + element.h &&
    coordinates.y + coordinates.h > element.y;
